import React from 'react'
import { Link } from 'react-router-dom'
import { Container, Row, Col } from 'react-bootstrap'
import { Helmet } from 'react-helmet-async'
import {
  FaLeaf,
  FaClock,
  FaSnowflake,
  FaFlask,
  FaTruck,
  FaUsers,
  FaHeartbeat,
  FaBolt,
  FaWeight,
  FaCheese,
} from 'react-icons/fa'
import ImagePlaceholder from '../components/ImagePlaceholder'

const FEATURES = [
  { Icon: FaLeaf,     title: 'Sem conservantes',    sub: 'Ingredientes naturais' },
  { Icon: FaClock,    title: 'Pronto em 6 min',      sub: 'Praticidade no dia a dia' },
  { Icon: FaSnowflake,title: '180 dias de validade', sub: 'Congelado com frescor' },
  { Icon: FaCheese,   title: 'Sem glúten e lactose', sub: 'Para todos os públicos' },
]

const BENEFITS = [
  {
    Icon: FaWeight,
    title: 'Gerenciamento de Peso',
    text: 'Calorias controladas e composição nutricional balanceada para auxiliar no controle e gerenciamento do peso corporal.',
  },
  {
    Icon: FaHeartbeat,
    title: 'Saúde Cardiovascular',
    text: 'Auxílio no controle do colesterol e melhoria da digestão com ingredientes selecionados por nutricionistas e médicos.',
  },
  {
    Icon: FaBolt,
    title: 'Energia & Performance',
    text: 'Maior estabilidade de energia para os treinos e o dia a dia. Desenvolvido com a curadoria de educadores físicos.',
  },
  {
    Icon: FaTruck,
    title: 'Logística Fria Própria',
    text: 'Frota própria de veículos refrigerados garante a integridade nutricional de cada ingrediente até o seu destino.',
  },
  {
    Icon: FaFlask,
    title: 'Curadoria Técnica',
    text: 'Cada receita é desenvolvida com rigorosa análise científica, unindo nutricionistas, médicos e educadores físicos.',
  },
  {
    Icon: FaUsers,
    title: 'Para Todos os Públicos',
    text: 'Indicada para crianças, adultos e idosos. Uma ferramenta de mudança de vida para toda a família.',
  },
]

export default function Home() {
  return (
    <>
      <Helmet>
        <title>Clean Foods Brasil – Marmitas Congeladas para Atletas</title>
        <meta name="description" content="Marmitas congeladas saudáveis, sem glúten e sem lactose, prontas em 6 minutos. Referência nacional em nutrição para atletas de alto rendimento." />
      </Helmet>

      {/* ========= HERO ========= */}
      <section className="hero-section" style={{ paddingTop: 72 }}>
        <div className="hero-bg-placeholder" />
        {/* Placeholder for hero background image */}
        <div
          className="img-placeholder position-absolute w-100 h-100"
          style={{ inset: 0, borderRadius: 0, border: 'none', opacity: 0.18, zIndex: 0 }}
        >
          <ImagePlaceholder height="100%" label="Foto de fundo do hero" />
        </div>
        <div className="hero-overlay" />
        <Container className="hero-content py-5">
          <Row className="align-items-center gy-5">
            <Col lg={7}>
              <p className="text-cf-yellow fw-bold text-uppercase small letter-spacing-2 mb-3" style={{ letterSpacing: 2 }}>
                Referência Nacional • Desde 2015
              </p>
              <h1 className="hero-tagline mb-4">
                Comida de <span className="highlight">Verdade,</span>
                Pronta em <span className="highlight">Minutos!</span>
              </h1>
              <p className="mb-4" style={{ fontSize: '1.1rem', maxWidth: 520, lineHeight: 1.7 }}>
                <span>
                  Marmitas congeladas com curadoria técnica de nutricionistas, médicos e educadores físicos.
                  Sem glúten, sem lactose, sem conservantes. Pioneira no interior de São Paulo.
                </span>
              </p>
              <div className="d-flex flex-wrap gap-3">
                <Link to="/nossos-produtos" className="btn btn-cf-yellow btn-lg">
                  Nossos Produtos
                </Link>
                <Link to="/seja-revendedor" className="btn btn-cf-outline btn-lg">
                  Seja um Revendedor
                </Link>
              </div>
            </Col>
            <Col lg={5} className="d-none d-lg-block">
              <ImagePlaceholder height="420px" label="Foto dos produtos / atleta" />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ========= FEATURE STRIP ========= */}
      <section className="feature-strip">
        <Container>
          <Row className="gy-4 justify-content-center">
            {FEATURES.map(({ Icon, title, sub }) => (
              <Col key={title} md={6} lg={3}>
                <div className="feature-item justify-content-center justify-content-lg-start">
                  <div className="feature-icon">
                    <Icon />
                  </div>
                  <div>
                    <p className="feature-text-title mb-0">{title}</p>
                    <p className="feature-text-sub mb-0">{sub}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ========= STATS ========= */}
      <section className="stats-bar">
        <Container>
          <Row className="gy-4 text-center">
            {[
              { number: '2015', label: 'Ano de fundação' },
              { number: '180',  label: 'Dias de validade congelado' },
              { number: '6min', label: 'Para estar pronto' },
              { number: '100%', label: 'Natural, sem conservantes' },
            ].map(({ number, label }) => (
              <Col key={label} xs={6} md={3}>
                <span className="stat-number">{number}</span>
                <p className="stat-label mb-0">{label}</p>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ========= SOBRE (preview) ========= */}
      <section className="section-padding bg-cf-black">
        <Container>
          <Row className="align-items-center gy-5">
            <Col lg={6}>
              <ImagePlaceholder height="420px" label="Foto da empresa / cozinha" />
            </Col>
            <Col lg={6}>
              <p className="text-cf-yellow fw-bold text-uppercase small mb-2" style={{ letterSpacing: 2 }}>
                Nossa História
              </p>
              <h2 className="section-title mb-0">
                Nascemos para <span className="highlight">transformar</span>
              </h2>
              <hr className="section-divider" />
              <p className="mb-4" style={{ lineHeight: 1.8 }}>
                Desde 2015, a Clean Foods Brasil nasceu com um propósito claro: transformar a alimentação
                saudável em algo prático, saboroso e acessível. Sendo pioneira no interior de São Paulo,
                nossa marca surgiu para facilitar a rotina de quem busca performance e bem-estar, sem
                abrir mão do prazer de comer bem.
              </p>
              <p className="mb-4" style={{ lineHeight: 1.8 }}>
                Mais do que apenas marmitas congeladas, entregamos um conceito de nutrição inteligente.
                Cada receita é desenvolvida através de curadoria técnica rigorosa, unindo nutricionistas,
                médicos e educadores físicos.
              </p>
              <Link to="/quem-somos" className="btn btn-cf-outline">
                Conheça nossa história
              </Link>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ========= BENEFÍCIOS ========= */}
      <section className="section-padding bg-cf-dark">
        <Container>
          <div className="text-center mb-5">
            <p className="text-cf-yellow fw-bold text-uppercase small mb-2" style={{ letterSpacing: 2 }}>
              Por que Clean Foods?
            </p>
            <h2 className="section-title">
              Benefícios <span className="highlight">Clean Foods</span>
            </h2>
            <hr className="section-divider centered mx-auto" />
            <p className="mx-auto" style={{ maxWidth: 560 }}>
              Muito além de uma marmita fitness — uma ferramenta de mudança de vida.
            </p>
          </div>
          <Row className="gy-4">
            {BENEFITS.map(({ Icon, title, text }) => (
              <Col key={title} md={6} lg={4}>
                <div className="cf-card p-4 d-flex gap-3">
                  <div className="cf-card-icon">
                    <Icon />
                  </div>
                  <div>
                    <h5 className="fw-bold mb-2" style={{ fontSize: '1.05rem' }}>{title}</h5>
                    <p className="mb-0" style={{ fontSize: '0.9rem', lineHeight: 1.7 }}>{text}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ========= PRODUTOS DESTAQUE ========= */}
      <section className="section-padding bg-cf-black">
        <Container>
          <div className="text-center mb-5">
            <p className="text-cf-yellow fw-bold text-uppercase small mb-2" style={{ letterSpacing: 2 }}>
              O que oferecemos
            </p>
            <h2 className="section-title">
              Nossos <span className="highlight">Produtos</span>
            </h2>
            <hr className="section-divider centered mx-auto" />
          </div>
          <Row className="gy-4">
            {[
              { label: 'Produto destaque 1' },
              { label: 'Produto destaque 2' },
              { label: 'Produto destaque 3' },
            ].map(({ label }, i) => (
              <Col key={i} md={4}>
                <div className="product-card">
                  <ImagePlaceholder height="220px" label={label} />
                  <div className="product-card-body">
                    <span className="product-badge mb-2">Fitness</span>
                    <h5 className="product-card-title mt-2">Nome do produto</h5>
                    <p className="mb-0" style={{ fontSize: '0.88rem' }}>
                      Prato balanceado, sem glúten, sem lactose e pronto em 6 minutos.
                    </p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          <div className="text-center mt-5">
            <Link to="/nossos-produtos" className="btn btn-cf-yellow btn-lg px-5">
              Ver todos os produtos
            </Link>
          </div>
        </Container>
      </section>

      {/* ========= CTA REVENDEDOR ========= */}
      <section className="cta-section">
        <Container>
          <Row className="align-items-center gy-4">
            <Col lg={8}>
              <h2 className="cta-title mb-2">
                Quer fazer parte da rede Clean Foods?
              </h2>
              <p className="cta-text mb-0">
                Seja um revendedor autorizado e leve nutrição de alto rendimento para a sua região.
              </p>
            </Col>
            <Col lg={4} className="text-lg-end">
              <Link
                to="/seja-revendedor"
                className="btn btn-lg fw-bold text-uppercase px-5"
                style={{ background: 'var(--cf-black)', color: 'var(--cf-yellow)', letterSpacing: 1, borderRadius: 4 }}
              >
                Quero ser revendedor
              </Link>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  )
}
